import React, { useState } from 'react';
import {
  HelpCircle,
  Mail,
  MessageSquare,
  Book,
  ChevronDown,
  ChevronUp,
  Search,
  FileText,
  Users,
  Settings,
  Shield
} from 'lucide-react';
import HelpChatbot from '../components/HelpChatbot';

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const Help: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [isChatbotOpen, setIsChatbotOpen] = useState(false);

  const faqs: FAQItem[] = [
    {
      question: 'How do I connect my Microsoft 365 account?',
      answer: 'Navigate to the Integrations page and click on the Microsoft 365 card. Follow the authorization flow to grant Niwt access to your emails and calendar. The process is secure and uses OAuth 2.0 authentication.',
      category: 'integrations'
    },
    {
      question: 'What data does Niwt access from my emails?',
      answer: 'Niwt only accesses email metadata (subject, sender, date) and content for vectorization and AI processing. Your data is encrypted in transit and at rest, and is never shared with third parties.',
      category: 'security'
    },
    {
      question: 'How do I create a digital twin?',
      answer: 'Digital twins are automatically created when you connect an integration. The AI learns from your communication patterns, writing style, and domain expertise to create an accurate representation.',
      category: 'getting-started'
    },
    {
      question: 'Can I control what my digital twin has access to?',
      answer: 'Yes, you can manage permissions and data access through the Profile Settings page. You can revoke access to specific integrations or limit the scope of data available to your twin.',
      category: 'security'
    },
    {
      question: 'How accurate is the AI assistant?',
      answer: 'Our AI assistant is powered by Claude from Anthropic, one of the most advanced language models available. It provides context-aware responses based on your connected data sources and has been trained on a vast knowledge base.',
      category: 'getting-started'
    },
    {
      question: 'What happens if I disconnect an integration?',
      answer: 'When you disconnect an integration, Niwt will no longer have access to new data from that source. Previously ingested data will remain available unless you explicitly request its deletion from your profile settings.',
      category: 'integrations'
    },
    {
      question: 'How do I upgrade my subscription?',
      answer: 'Account managers can upgrade subscriptions by visiting the Billing page. Choose a plan that fits your needs and follow the checkout process. Changes take effect immediately.',
      category: 'billing'
    },
    {
      question: 'Is my data secure?',
      answer: 'Absolutely. Niwt is SOC 2 Type II certified and CMMC-ready. We use end-to-end encryption, secure data centers, and follow industry best practices for data protection. Your data is never shared or sold.',
      category: 'security'
    }
  ];

  const categories = [
    { id: 'all', name: 'All Topics', icon: Book },
    { id: 'getting-started', name: 'Getting Started', icon: Users },
    { id: 'integrations', name: 'Integrations', icon: Settings },
    { id: 'security', name: 'Security & Privacy', icon: Shield },
    { id: 'billing', name: 'Billing', icon: FileText }
  ];

  const filteredFAQs = faqs.filter(faq => {
    const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory;
    const matchesSearch = faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const toggleFAQ = (index: number) => {
    setExpandedFAQ(expandedFAQ === index ? null : index);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
          <HelpCircle className="w-8 h-8 text-niwt-primary" />
        </div>
        <h1 className="text-4xl font-bold text-niwt-text font-libre mb-4">
          Help & Support
        </h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Find answers to common questions or get in touch with our support team
        </p>
      </div>

      {/* Search Bar */}
      <div className="max-w-2xl mx-auto">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search for help..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-niwt-primary focus:border-transparent text-lg"
          />
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        <a
          href="mailto:support@niwt.com"
          className="flex items-start p-6 bg-white rounded-xl border-2 border-gray-200 hover:border-niwt-primary transition-colors"
        >
          <div className="flex-shrink-0 w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
            <Mail className="w-6 h-6 text-niwt-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-niwt-text mb-1">Email Support</h3>
            <p className="text-sm text-gray-600">
              Get help via email within 24 hours
            </p>
            <p className="text-sm text-niwt-primary font-medium mt-2">support@niwt.com</p>
          </div>
        </a>

        <button
          onClick={() => setIsChatbotOpen(true)}
          className="flex items-start p-6 bg-white rounded-xl border-2 border-gray-200 hover:border-niwt-primary transition-colors text-left"
        >
          <div className="flex-shrink-0 w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
            <MessageSquare className="w-6 h-6 text-niwt-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-niwt-text mb-1">AI Help Chat</h3>
            <p className="text-sm text-gray-600">
              Get instant answers from our AI assistant
            </p>
            <p className="text-sm text-niwt-primary font-medium mt-2">Start Chat →</p>
          </div>
        </button>
      </div>

      {/* FAQ Section */}
      <div className="max-w-5xl mx-auto">
        <h2 className="text-2xl font-bold text-niwt-text font-libre mb-6">
          Frequently Asked Questions
        </h2>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-3 mb-8">
          {categories.map((category) => {
            const Icon = category.icon;
            return (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`inline-flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
                  selectedCategory === category.id
                    ? 'bg-niwt-primary text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:border-niwt-primary'
                }`}
              >
                <Icon className="w-4 h-4 mr-2" />
                {category.name}
              </button>
            );
          })}
        </div>

        {/* FAQ List */}
        <div className="space-y-4">
          {filteredFAQs.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
              <HelpCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">No results found. Try a different search term or category.</p>
            </div>
          ) : (
            filteredFAQs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:border-niwt-primary transition-colors"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full flex items-center justify-between p-6 text-left"
                >
                  <h3 className="text-lg font-semibold text-niwt-text pr-4">
                    {faq.question}
                  </h3>
                  {expandedFAQ === index ? (
                    <ChevronUp className="w-5 h-5 text-niwt-primary flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {expandedFAQ === index && (
                  <div className="px-6 pb-6 text-gray-600 leading-relaxed">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Still Need Help */}
      <div className="max-w-5xl mx-auto bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-8 text-center">
        <h2 className="text-2xl font-bold text-niwt-text font-libre mb-2">
          Still need help?
        </h2>
        <p className="text-gray-600 mb-6">
          Our support team is here to assist you with any questions or issues
        </p>
        <a
          href="mailto:support@niwt.com"
          className="inline-flex items-center px-6 py-3 bg-niwt-primary text-white font-semibold rounded-lg hover:bg-niwt-secondary transition-colors"
        >
          <Mail className="w-5 h-5 mr-2" />
          Contact Support
        </a>
      </div>

      {/* Help Chatbot Modal */}
      <HelpChatbot isOpen={isChatbotOpen} onClose={() => setIsChatbotOpen(false)} />
    </div>
  );
};

export default Help;
